import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './styles.less';

const SimpleDropdown = ({ children, items = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const updatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const dropdownWidth = 160; // min-width of dropdown
        const dropdownHeight = dropdownRef.current?.offsetHeight || 200; // estimated height
        const padding = 5; // padding from viewport edges
        const gap = 4; // gap between trigger and dropdown

        // Calculate initial position (below and right-aligned with trigger)
        let top = rect.bottom + window.scrollY + gap;
        let left = rect.right - dropdownWidth + window.scrollX;

        // Check if dropdown goes off the right edge
        if (rect.right > window.innerWidth - padding) {
          // Align to left side of trigger instead
          left = rect.left + window.scrollX;
        }

        // Check if dropdown goes off the left edge
        if (left < padding) {
          left = padding;
        }

        // Check if dropdown goes off the bottom
        if (rect.bottom + dropdownHeight > window.innerHeight - padding) {
          // Position above the trigger instead
          top = rect.top + window.scrollY - dropdownHeight - gap;
        }

        setPosition({ top, left });
      }
    };

    if (isOpen) {
      // Use setTimeout to ensure dropdown is rendered before calculating position
      setTimeout(updatePosition, 0);
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updatePosition, true); // Use capture phase
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const handleItemClick = (item) => {
    if (item.onClick) {
      item.onClick();
    }
    setIsOpen(false);
  };

  const dropdownMenu = isOpen && ReactDOM.createPortal(
    <>
      {/* Invisible backdrop to catch clicks outside */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99998,
          background: 'transparent',
        }}
        onClick={() => setIsOpen(false)}
      />
      <div
        ref={dropdownRef}
        className="dropdown-menu-portal"
        style={{
          position: 'absolute',
          top: `${position.top}px`,
          left: `${position.left}px`,
          zIndex: 99999,
        }}
      >
        {items.map((item, index) => {
          if (item.type === 'divider') {
            return <div key={`divider-${index}`} className="dropdown-divider" />;
          }
          return (
            <div
              key={item.key || index}
              className={`dropdown-item ${item.danger ? 'dropdown-item-danger' : ''}`}
              onClick={() => handleItemClick(item)}
            >
              {item.icon && <span className="dropdown-icon">{item.icon}</span>}
              <span>{item.label}</span>
            </div>
          );
        })}
      </div>
    </>,
    document.body
  );

  return (
    <div className="simple-dropdown">
      <div
        ref={triggerRef}
        className="dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        {children}
      </div>
      {dropdownMenu}
    </div>
  );
};

export default SimpleDropdown;